import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';
import { formatCompanyDateRange } from '../utils/companyDateFormat';
import { X, Upload, Loader2, ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';
import { attendanceApi } from '../services/api';
import type { AttendanceImportColumnMapping, AttendanceImportValidateResponse } from '../types';
import { AttendanceImportRowsPreviewTable } from './AttendanceImportRowsPreview';

const mappingStorageKey = (locationId: number) => `staffly.attendanceImport.mapping.v1.${locationId}`;

type Step = 'upload' | 'mapping' | 'preview' | 'done';

export interface ImportAttendanceModalProps {
  open: boolean;
  onClose: () => void;
  locationId: number;
  /** Inclusive yyyy-MM-dd; clock in/out must fall in this range (Attendance page week) */
  allowedWeekStartDate: string;
  allowedWeekEndDate: string;
  onImportCommitted?: () => void;
}

const MAPPING_FIELDS: { key: keyof AttendanceImportColumnMapping; label: string; required: boolean }[] = [
  { key: 'employeeName', label: 'Employee name (First + Last)', required: true },
  { key: 'clockIn', label: 'In time (date + time in cell)', required: true },
  { key: 'clockOut', label: 'Out time (date + time in cell)', required: false },
];

/** Matches backend: Excel serial (0,1) = time fraction; or plain time string without a date. */
function looksLikeTimeOnlyValue(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  const n = Number(s.replace(/,/g, ''));
  if (!Number.isNaN(n) && Number.isFinite(n) && n > 0 && n < 1) return true;
  return /^\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?(\s*[ap]m)?$/i.test(s);
}

/** First offending cell for user-facing error before calling the API. */
function findFirstTimeOnlyMappedValue(
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>,
  clockIn: string,
  clockOut: string
): string | null {
  const check = (col: string, label: string) => {
    if (!col) return null;
    for (const r of rows) {
      const v = r.cells[col];
      if (v == null || String(v).trim() === '') continue;
      if (looksLikeTimeOnlyValue(String(v))) {
        return `${label} column, row ${r.rowNumber}: value "${String(v).trim()}" is time-only. Each cell must include a full date and time.`;
      }
    }
    return null;
  };
  return check(clockIn, 'In') ?? (clockOut.trim() ? check(clockOut, 'Out') : null);
}

/** Best-effort parse for week check (mirrors common Excel / spreadsheet formats). */
function tryParseImportCellToDate(raw: string): Date | null {
  const t = raw.trim();
  if (!t) return null;
  const num = Number(t.replace(/,/g, ''));
  if (!Number.isNaN(num) && Number.isFinite(num) && num > 10 && num < 1000000) {
    const epoch = new Date(Date.UTC(1899, 11, 30));
    const ms = epoch.getTime() + num * 86400000;
    return new Date(ms);
  }
  const d = new Date(t);
  if (!Number.isNaN(d.getTime())) return d;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const d2 = new Date(t.replace(' ', 'T'));
    if (!Number.isNaN(d2.getTime())) return d2;
  }
  return null;
}

function findFirstDateOutsideAllowedWeek(
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>,
  clockInCol: string,
  clockOutCol: string,
  weekStart: string,
  weekEnd: string
): string | null {
  for (const r of rows) {
    const inRaw = r.cells[clockInCol]?.trim();
    if (!inRaw) continue;
    const cin = tryParseImportCellToDate(inRaw);
    if (!cin) {
      return `Row ${r.rowNumber}: could not parse clock in date. Use a full date and time in the mapped column.`;
    }
    const inStr = format(cin, 'yyyy-MM-dd');
    if (inStr < weekStart || inStr > weekEnd) {
      return `Row ${r.rowNumber}: clock in must fall within the week shown on Attendance (${weekStart} – ${weekEnd}).`;
    }
    if (clockOutCol.trim()) {
      const outRaw = r.cells[clockOutCol]?.trim();
      if (outRaw) {
        let cout = tryParseImportCellToDate(outRaw);
        if (!cout) {
          return `Row ${r.rowNumber}: could not parse clock out date. Use a full date and time in the mapped column.`;
        }
        if (cout < cin) {
          cout = new Date(cout.getTime() + 86400000);
        }
        const outStr = format(cout, 'yyyy-MM-dd');
        if (outStr < weekStart || outStr > weekEnd) {
          return `Row ${r.rowNumber}: clock out must fall within the week shown on Attendance (${weekStart} – ${weekEnd}).`;
        }
      }
    }
  }
  return null;
}

type SheetData = {
  headers: string[];
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>;
};

function parseMatrixToSheetData(matrix: (string | number | undefined)[][]): SheetData | null {
  if (!matrix.length) return null;

  const rawHeaders = matrix[0].map((c) => String(c ?? '').trim());
  if (!rawHeaders.some(Boolean)) return null;

  const rows: Array<{ rowNumber: number; cells: Record<string, string> }> = [];
  let dataRowOrdinal = 0;
  for (let r = 1; r < matrix.length; r++) {
    const line = matrix[r];
    const cells: Record<string, string> = {};
    let any = false;
    rawHeaders.forEach((h, i) => {
      if (!h) return;
      const v = line[i];
      const cellStr = v === undefined || v === null ? '' : String(v).trim();
      cells[h] = cellStr;
      if (cellStr) any = true;
    });
    if (any) {
      dataRowOrdinal += 1;
      rows.push({ rowNumber: dataRowOrdinal, cells });
    }
  }

  return { headers: rawHeaders.filter(Boolean), rows };
}

/**
 * Prior-run column header is used only if the current sheet has a column with the exact same name
 * (trimmed, case-sensitive). Otherwise returns '' so the user picks from the dropdown.
 */
function pickMappedColumn(stored: string | undefined | null, headers: string[]): string {
  if (stored == null) return '';
  const t = String(stored).trim();
  if (t === '') return '';
  const hit = headers.find((h) => h === t);
  return hit !== undefined ? hit : '';
}

/** Applies saved mapping only for names that appear exactly on this sheet's header row. */
function intersectMappingWithHeaders(
  m: Partial<AttendanceImportColumnMapping>,
  headers: string[]
): AttendanceImportColumnMapping {
  return {
    employeeName: pickMappedColumn(m.employeeName, headers),
    clockIn: pickMappedColumn(m.clockIn, headers),
    clockOut: pickMappedColumn(m.clockOut, headers),
  };
}

function saveStoredMapping(locationId: number, m: AttendanceImportColumnMapping) {
  try {
    localStorage.setItem(mappingStorageKey(locationId), JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

/** Loads saved column mapping; strips legacy `date` key. */
function loadStoredMapping(locationId: number): Partial<AttendanceImportColumnMapping> {
  try {
    const raw = localStorage.getItem(mappingStorageKey(locationId));
    if (!raw) return {};
    const o = JSON.parse(raw) as Record<string, unknown>;
    delete o.date;
    return o as Partial<AttendanceImportColumnMapping>;
  } catch {
    return {};
  }
}

export const ImportAttendanceModal: React.FC<ImportAttendanceModalProps> = ({
  open,
  onClose,
  locationId,
  allowedWeekStartDate,
  allowedWeekEndDate,
  onImportCommitted,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetsByName, setSheetsByName] = useState<Record<string, SheetData>>({});
  const [selectedSheetName, setSelectedSheetName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sheetRows, setSheetRows] = useState<Array<{ rowNumber: number; cells: Record<string, string> }>>([]);
  const [mapping, setMapping] = useState<AttendanceImportColumnMapping>({
    employeeName: '',
    clockIn: '',
    clockOut: '',
  });
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validateResult, setValidateResult] = useState<AttendanceImportValidateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Original file for multipart upload so history can link to the real Excel. */
  const [sourceFile, setSourceFile] = useState<File | null>(null);

  const headerOptions = useMemo(() => {
    const h = [...headers];
    return ['', ...h];
  }, [headers]);

  const allowedWeekLabel = useMemo(() => {
    try {
      const a = parseISO(allowedWeekStartDate);
      const b = parseISO(allowedWeekEndDate);
      if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
      return formatCompanyDateRange(allowedWeekStartDate, allowedWeekEndDate);
    } catch {
      return '';
    }
  }, [allowedWeekStartDate, allowedWeekEndDate]);

  useEffect(() => {
    if (!open) return;
    const stored = loadStoredMapping(locationId);
    // No sheet headers yet — only restore mapping when a column name exactly matches (see intersectMappingWithHeaders).
    setMapping(intersectMappingWithHeaders(stored, []));
    setStep('upload');
    setFileName('');
    setSheetNames([]);
    setSheetsByName({});
    setSelectedSheetName('');
    setHeaders([]);
    setSheetRows([]);
    setValidateResult(null);
    setError(null);
    setSourceFile(null);
  }, [open, locationId]);

  const onSheetChange = useCallback(
    (sheetName: string) => {
      const data = sheetsByName[sheetName];
      if (!data) return;
      setSelectedSheetName(sheetName);
      setHeaders(data.headers);
      setSheetRows(data.rows);
      // Default from last successful mapping only; exact header name on this sheet or leave blank (pickMappedColumn).
      const stored = loadStoredMapping(locationId);
      setMapping(intersectMappingWithHeaders(stored, data.headers));
    },
    [sheetsByName, locationId]
  );

  const parseFile = useCallback((file: File) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const byName: Record<string, SheetData> = {};
        const names: string[] = [];

        for (const sheetName of wb.SheetNames) {
          const sheet = wb.Sheets[sheetName];
          const matrix = XLSX.utils.sheet_to_json<(string | number | undefined)[]>(sheet, {
            header: 1,
            defval: '',
            raw: false,
          }) as (string | number | undefined)[][];

          const parsed = parseMatrixToSheetData(matrix);
          if (parsed && parsed.headers.length > 0) {
            byName[sheetName] = parsed;
            names.push(sheetName);
          }
        }

        if (names.length === 0) {
          setError('No sheet with a header row and columns was found.');
          return;
        }

        const stored = loadStoredMapping(locationId);
        const merged: AttendanceImportColumnMapping = intersectMappingWithHeaders(
          {
            employeeName: stored.employeeName ?? '',
            clockIn: stored.clockIn ?? '',
            clockOut: stored.clockOut ?? '',
          },
          byName[names[0]!]!.headers
        );

        setSheetsByName(byName);
        setSheetNames(names);
        const firstName = names[0]!;
        const firstData = byName[firstName]!;
        setSelectedSheetName(firstName);
        setHeaders(firstData.headers);
        setSheetRows(firstData.rows);
        setMapping(merged);
        setFileName(file.name);
        setSourceFile(file);
        setStep('mapping');
      } catch {
        setError('Could not read this Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  }, [locationId]);

  const buildPayloadRows = useCallback(() => {
    return sheetRows.map((r) => ({
      rowNumber: r.rowNumber,
      cells: r.cells,
    }));
  }, [sheetRows]);

  const handleValidate = async () => {
    if (!mapping.employeeName || !mapping.clockIn) {
      setError('Map Employee name and In time to spreadsheet columns.');
      return;
    }

    const preflight = findFirstTimeOnlyMappedValue(sheetRows, mapping.clockIn, mapping.clockOut || '');
    if (preflight) {
      setError(preflight);
      return;
    }

    const weekErr = findFirstDateOutsideAllowedWeek(
      sheetRows,
      mapping.clockIn,
      mapping.clockOut || '',
      allowedWeekStartDate,
      allowedWeekEndDate
    );
    if (weekErr) {
      setError(weekErr);
      return;
    }

    saveStoredMapping(locationId, {
      employeeName: mapping.employeeName,
      clockIn: mapping.clockIn,
      clockOut: mapping.clockOut || '',
    });

    setValidating(true);
    setError(null);
    try {
      const payloadMapping: AttendanceImportColumnMapping = {
        employeeName: mapping.employeeName,
        clockIn: mapping.clockIn,
        clockOut: mapping.clockOut?.trim() || '',
      };
      const res = await attendanceApi.importValidate(
        {
          locationId,
          fileName: fileName || undefined,
          mapping: payloadMapping,
          rows: buildPayloadRows(),
          allowedWeekStartDate,
          allowedWeekEndDate,
        },
        sourceFile
      );
      setValidateResult(res);
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Validation failed.');
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async () => {
    if (!validateResult?.batchId) return;
    setCommitting(true);
    setError(null);
    try {
      await attendanceApi.importCommit(validateResult.batchId);
      setStep('done');
      onImportCommitted?.();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Import failed.');
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="border-b">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Import attendance</h2>
              {allowedWeekLabel ? (
                <p className="mt-0.5 text-xs text-gray-500">
                  Dates must fall within the week on the Attendance page: {allowedWeekLabel}
                </p>
              ) : null}
            </div>
            <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
          {step === 'mapping' && sheetNames.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
              <label htmlFor="import-attendance-sheet" className="text-sm font-medium text-gray-700">
                Sheet
              </label>
              <select
                id="import-attendance-sheet"
                value={selectedSheetName}
                onChange={(e) => onSheetChange(e.target.value)}
                className="min-w-[12rem] rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {sheetNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                Columns and rows update from the selected sheet.
              </span>
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Upload an Excel file (.xlsx). The first row should contain column headers. In and Out columns must contain
                a full date and time in each cell (not time-only). Rows should include employee name (first and last name).
              </p>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 hover:bg-gray-100">
                <Upload className="mb-2 h-10 w-10 text-gray-400" />
                <span className="text-sm font-medium text-blue-600">Choose Excel file</span>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) parseFile(f);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          )}

          {step === 'mapping' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                File: <span className="font-medium">{fileName}</span> — {sheetRows.length} non-empty data row(s). Map each
                field to a column. In/Out cells must include date and time. Mappings are remembered for this location.
              </p>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-4">Staffly field</th>
                    <th className="py-2">Excel column</th>
                  </tr>
                </thead>
                <tbody>
                  {MAPPING_FIELDS.map(({ key, label, required }) => (
                    <tr key={key} className="border-b border-gray-100">
                      <td className="py-2 pr-4 align-middle">
                        {label}
                        {required && <span className="text-red-500"> *</span>}
                      </td>
                      <td className="py-2 align-middle">
                        <select
                          value={mapping[key] || ''}
                          onChange={(e) =>
                            setMapping((m) => ({
                              ...m,
                              [key]: e.target.value,
                            }))
                          }
                          className="w-full max-w-xs rounded border border-gray-300 px-2 py-1.5"
                        >
                          {headerOptions.map((opt) => (
                            <option key={opt || '—'} value={opt}>
                              {opt || '(none)'}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button
                type="button"
                onClick={() => void handleValidate()}
                disabled={validating}
                className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {validating ? 'Validating…' : 'Validate import'}
              </button>
            </div>
          )}

          {step === 'preview' && validateResult && (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm">
                <p>
                  Batch #{validateResult.batchId} — Valid:{' '}
                  <span className="font-semibold text-green-700">{validateResult.validRowCount}</span> — Invalid:{' '}
                  <span className="font-semibold text-red-700">{validateResult.invalidRowCount}</span>
                </p>
              </div>

              <AttendanceImportRowsPreviewTable rows={validateResult.rows} />

              <p className="text-sm text-gray-600">
                Confirm to create attendance for all <strong>valid</strong> rows. Invalid rows will be skipped.
              </p>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStep('mapping')}
                  className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => void handleCommit()}
                  disabled={committing || validateResult.validRowCount === 0}
                  className="inline-flex items-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {committing ? 'Importing…' : 'Confirm import'}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="space-y-3 text-center">
              <CheckCircle className="mx-auto h-14 w-14 text-green-500" />
              <p className="text-lg font-medium text-gray-900">Import finished</p>
              <p className="text-sm text-gray-600">
                Attendance records were created for valid rows. To review past imports, open{' '}
                <strong>Attendance</strong> → <strong>Import</strong> → <strong>Show Import History</strong>.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ImportAttendanceModal;
